import { useCallback, useState } from "react"
import { Check, Copy, WrapText } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "../Button/Button"

export interface CodeHighlightToken {
  content: string
  color?: string
}

export type CodeHighlightedLine = CodeHighlightToken[]

export interface CodeViewerProps {
  code: string
  highlightedLines?: CodeHighlightedLine[] | null
  wrap?: boolean
  defaultWrap?: boolean
  onWrapChange?: (wrap: boolean) => void
  onCopy?: () => void
  showCopy?: boolean
  showWrap?: boolean
  className?: string
}

export function CodeViewer({
  code,
  highlightedLines,
  wrap: controlledWrap,
  defaultWrap = false,
  onWrapChange,
  onCopy,
  showCopy = true,
  showWrap = true,
  className,
}: CodeViewerProps) {
  const [internalWrap, setInternalWrap] = useState(defaultWrap)
  const wrap = controlledWrap ?? internalWrap
  const toggleWrap = useCallback(() => {
    const next = !wrap
    setInternalWrap(next)
    onWrapChange?.(next)
  }, [wrap, onWrapChange])

  const [copied, setCopied] = useState(false)
  const handleCopy = useCallback(async () => {
    if (onCopy) {
      onCopy()
    } else if (typeof navigator !== "undefined" && navigator.clipboard) {
      await navigator.clipboard.writeText(code)
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [code, onCopy])

  const rawLines = code.split("\n")
  const lineCount = highlightedLines?.length ?? rawLines.length

  return (
    <div className={cn("h-full flex flex-col relative", className)}>
      {(showWrap || showCopy) && (
        <div className="absolute top-2 right-3 z-10 flex items-center gap-1">
          {showWrap && (
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleWrap}
              className={cn(
                "h-6 text-caption gap-1 text-muted-foreground hover:text-foreground hover:bg-muted",
                wrap && "text-primary hover:text-primary",
              )}
            >
              <WrapText size={11} />
              <span className="hidden sm:inline">Wrap</span>
            </Button>
          )}
          {showCopy && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCopy}
              className="h-6 text-caption gap-1 text-muted-foreground hover:text-foreground hover:bg-muted"
            >
              {copied ? (
                <>
                  <Check size={11} className="text-success" />
                  <span className="hidden sm:inline">Copied</span>
                </>
              ) : (
                <>
                  <Copy size={11} />
                  <span className="hidden sm:inline">Copy</span>
                </>
              )}
            </Button>
          )}
        </div>
      )}

      <div className="flex-1 overflow-auto font-mono text-body pt-4">
        <table className={cn("border-collapse w-full", wrap && "table-fixed")}>
          {wrap && (
            <colgroup>
              <col className="w-12" />
              <col />
            </colgroup>
          )}
          <tbody>
            {Array.from({ length: lineCount }).map((_, i) => (
              <tr key={i}>
                <td
                  className={cn(
                    "select-none text-right font-mono text-muted-foreground",
                    "px-3 align-top border-r border-border whitespace-nowrap",
                    !wrap && "w-12",
                  )}
                >
                  {i + 1}
                </td>
                <td
                  className={cn(
                    "px-4 align-top",
                    wrap ? "whitespace-pre-wrap break-all" : "whitespace-pre",
                  )}
                >
                  {highlightedLines
                    ? highlightedLines[i]?.map((t, j) => (
                        <span
                          key={j}
                          style={t.color ? { color: t.color } : undefined}
                        >
                          {t.content}
                        </span>
                      ))
                    : rawLines[i]}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
